import { formatAddress } from '@ctw/shared/api/fhir/formatters/address';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { useMatchedPatients } from '@ctw/shared/api/fhir/patient-helper';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import type { LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { formatPhoneNumber } from '@ctw/shared/utils/phone-number';
import { sort } from '@ctw/shared/utils/sort';
import { twx } from '@ctw/shared/utils/tailwind';
import { tw } from '@ctw/shared/utils/tailwind';
import { capitalize, uniqBy } from 'lodash-es';
import { useRef } from 'react';

export interface PatientSourcesProps {
  className?: string;
}

export const PatientSources = withErrorBoundary({
  boundaryName: 'PatientSources',
  Component: ({ className }: PatientSourcesProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { requestContext } = useCTW();
    const patientSourcesQuery = useMatchedPatients();
    const builderId = requestContext.builderId ?? '';

    const hasNoData = patientSourcesQuery.data?.length === 0;

    const dedupedData = uniqBy(
      patientSourcesQuery.data ?? [],
      (p: PatientModel) =>
        [
          p.createdAtDisplay,
          p.getDataSourceDisplay(builderId),
          p.display,
          p.dob,
          p.age,
          p.gender,
          p.phoneNumber,
          p.email,
          p.homeAddress,
        ].join('|'), // Stringify the array to compare its contents
    );
    const sortedData = sort(dedupedData, 'createdAt', 'desc');

    return (
      <div ref={containerRef} className={twx(className)}>
        <ResourceTable
          title="Patient Sources"
          titleVisuallyHidden={true}
          isLoading={patientSourcesQuery.isLoading}
          data={sortedData}
          emptyMessage={
            <EmptyPatientTable hasZeroFilteredRecords={hasNoData} resourceName="patients" />
          }
          columns={patientSourcesColumns(builderId)}
        />
      </div>
    );
  },
});

const patientSourcesColumns = (builderId: string): Array<LegacyTableColumn<PatientModel>> => [
  {
    widthPercent: 10,
    minWidth: 128,
    title: 'Date',
    render: (matchedPatient) => <div>{matchedPatient.createdAtDisplay}</div>,
  },
  {
    widthPercent: 30,
    title: 'Source',
    render: (matchedPatient) => <div>{matchedPatient.getDataSourceDisplay(builderId)}</div>,
  },
  {
    widthPercent: 30,
    title: 'Patient Details',
    render: (matchedPatient) => (
      <div className={tw`stacked-concat`}>
        {matchedPatient.displayFull && <div>{matchedPatient.displayFull}</div>}
        {Boolean(
          matchedPatient.dob || matchedPatient.age !== undefined || matchedPatient.gender,
        ) && (
          <div>
            <span>
              {matchedPatient.dob} ({matchedPatient.age})
            </span>
            <span> {capitalize(matchedPatient.gender)}</span>
          </div>
        )}
      </div>
    ),
  },
  {
    widthPercent: 40,
    title: 'Contact Info',
    render: (matchedPatient) => (
      <div className={tw`stacked-concat`}>
        {matchedPatient.phoneNumber && <div>{formatPhoneNumber(matchedPatient.phoneNumber)}</div>}
        {matchedPatient.email && <div>{matchedPatient.email}</div>}
        {matchedPatient.homeAddress && formatAddress(matchedPatient.homeAddress)}
      </div>
    ),
  },
];
